<script setup lang="ts">
import { Link } from '@inertiajs/vue3';
import { ArrowRight, Check } from 'lucide-vue-next';
import {
    Code2,
    Crop,
    EyeOff,
    FileArchive,
    FileMinus,
    FileSpreadsheet,
    FileText,
    Files,
    GitCompare,
    Hash,
    Image,
    ImageDown,
    Layers,
    ListOrdered,
    Lock,
    LockOpen,
    PenLine,
    Presentation,
    RotateCw,
    ScanText,
    ScissorsLineDashed,
    Stamp,
    Table2,
    Wrench,
} from 'lucide-vue-next';
import { computed } from 'vue';
import type { Component } from 'vue';
import ToolPageHead from '@/components/seo/ToolPageHead.vue';
import { Button } from '@/components/ui/button';
import {
    Card,
    CardDescription,
    CardHeader,
    CardTitle,
} from '@/components/ui/card';
import { useToolPageSeoFromInertia } from '@/composables/useToolPageSeo';
import { useTranslations } from '@/composables/useTranslations';
import { localizeHref } from '@/lib/localizeHref';
import { dashboard } from '@/routes';
import {
    addPageNumbers as addPageNumbersPdf,
    addWatermark as addWatermarkPdf,
    compare as comparePdf,
    compress as compressPdf,
    crop as cropPdf,
    extractPages as extractPagesPdf,
    fromExcel as fromExcelPdf,
    fromHtml as fromHtmlPdf,
    fromJpg as fromJpgPdf,
    fromPowerpoint as fromPowerpointPdf,
    fromWord as fromWordPdf,
    merge as mergePdf,
    ocr as ocrPdf,
    protect as protectPdf,
    redact as redactPdf,
    removePages as removePagesPdf,
    repair as repairPdf,
    reorder as reorderPdf,
    rotate as rotatePdf,
    sign as signPdf,
    split as splitPdf,
    toExcel as toExcelPdf,
    toJpg as toJpgPdf,
    toPowerpoint as toPowerpointPdf,
    toWord as toWordPdf,
    unlock as unlockPdf,
} from '@/routes/tools/pdf';

defineOptions({
    layout: {
        breadcrumbs: [
            {
                translationKey: 'tools.nav.home',
                title: 'Home',
                href: dashboard.url(),
            },
        ],
    },
});

type ToolEntry = {
    key: string;
    title: string;
    description: string;
    href: string;
    icon: Component;
    iconClass: string;
};

const { t, tList, locale } = useTranslations();

const heroFeatures = computed(() =>
    tList('tools.dashboard.hero.features', [
        'Merge, split, rotate, and compress with clear upload limits',
        'Convert to and from Word, Excel, PowerPoint, JPG, and more',
        'Privacy-first processing—uploads are not kept after your job finishes',
    ]),
);

const heroCtas = computed(() => [
    {
        href: localizeHref(mergePdf.url(), locale.value),
        label: t('tools.dashboard.hero.cta_merge', 'Merge PDF'),
        variant: 'default' as const,
    },
    {
        href: localizeHref(compressPdf.url(), locale.value),
        label: t('tools.dashboard.hero.cta_compress', 'Compress PDF'),
        variant: 'outline' as const,
    },
    {
        href: localizeHref(splitPdf.url(), locale.value),
        label: t('tools.dashboard.hero.cta_split', 'Split PDF'),
        variant: 'outline' as const,
    },
]);

const seo = useToolPageSeoFromInertia({
    seoPrefix: 'tools.dashboard.seo',
    fallbackTitleKey: 'tools.dashboard.title',
    fallbackTitleLabel: 'Tools',
    t,
    homeRelativeUrl: dashboard.url(),
    selfRelativeUrl: dashboard.url(),
    includeBreadcrumbs: false,
});

function localizeToolEntries(entries: ToolEntry[]): ToolEntry[] {
    return entries.map((entry) => ({
        ...entry,
        href: localizeHref(entry.href, locale.value),
    }));
}

const organizeToolsBase: ToolEntry[] = [
    {
        key: 'merge',
        title: 'Merge PDF',
        description:
            'Combine multiple PDF files into one document in the order you choose.',
        href: mergePdf.url(),
        icon: Layers,
        iconClass:
            'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
    },
    {
        key: 'split',
        title: 'Split PDF',
        description:
            'Split a PDF into separate files by page ranges or fixed page counts.',
        href: splitPdf.url(),
        icon: ScissorsLineDashed,
        iconClass:
            'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
    },
    {
        key: 'remove_pages',
        title: 'Remove pages',
        description: 'Remove unwanted pages from your PDF permanently.',
        href: removePagesPdf.url(),
        icon: FileMinus,
        iconClass:
            'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
    },
    {
        key: 'extract_pages',
        title: 'Extract pages',
        description:
            'Save only the pages you need as a new PDF without the rest.',
        href: extractPagesPdf.url(),
        icon: Files,
        iconClass:
            'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
    },
    {
        key: 'reorder',
        title: 'Reorder pages',
        description: 'Change the order of pages by defining a custom sequence.',
        href: reorderPdf.url(),
        icon: ListOrdered,
        iconClass:
            'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
    },
    {
        key: 'compress',
        title: 'Compress PDF',
        description:
            'Reduce file size for easier sharing while keeping pages readable.',
        href: compressPdf.url(),
        icon: FileArchive,
        iconClass:
            'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
    },
    {
        key: 'repair',
        title: 'Repair PDF',
        description:
            'Attempt to restore a corrupted or unreadable PDF when possible.',
        href: repairPdf.url(),
        icon: Wrench,
        iconClass:
            'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
    },
];

const editPdfToolsBase: ToolEntry[] = [
    {
        key: 'rotate',
        title: 'Rotate PDF',
        description: 'Rotate all pages or selections to fix upside-down scans.',
        href: rotatePdf.url(),
        icon: RotateCw,
        iconClass:
            'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300',
    },
    {
        key: 'add_page_numbers',
        title: 'Add page numbers',
        description:
            'Number pages with position, format, and range options for professional layouts.',
        href: addPageNumbersPdf.url(),
        icon: Hash,
        iconClass:
            'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300',
    },
    {
        key: 'add_watermark',
        title: 'Add watermark',
        description:
            'Overlay text or images across pages to mark drafts or ownership.',
        href: addWatermarkPdf.url(),
        icon: Stamp,
        iconClass:
            'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300',
    },
    {
        key: 'crop',
        title: 'Crop PDF',
        description: 'Trim page margins or crop to a region on every page.',
        href: cropPdf.url(),
        icon: Crop,
        iconClass:
            'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300',
    },
    {
        key: 'ocr',
        title: 'OCR PDF',
        description:
            'Make scanned PDFs searchable so you can select, copy, and find text.',
        href: ocrPdf.url(),
        icon: ScanText,
        iconClass:
            'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300',
    },
];

const securityPdfToolsBase: ToolEntry[] = [
    {
        key: 'unlock',
        title: 'Unlock PDF',
        description: 'Remove password restrictions when you have the password.',
        href: unlockPdf.url(),
        icon: LockOpen,
        iconClass:
            'bg-slate-200 text-slate-800 dark:bg-slate-800 dark:text-slate-200',
    },
    {
        key: 'protect',
        title: 'Protect PDF',
        description:
            'Set an open or permissions password to control printing and editing.',
        href: protectPdf.url(),
        icon: Lock,
        iconClass:
            'bg-slate-200 text-slate-800 dark:bg-slate-800 dark:text-slate-200',
    },
    {
        key: 'sign',
        title: 'Sign PDF',
        description: 'Place a digital or visual signature on your document.',
        href: signPdf.url(),
        icon: PenLine,
        iconClass:
            'bg-slate-200 text-slate-800 dark:bg-slate-800 dark:text-slate-200',
    },
    {
        key: 'redact',
        title: 'Redact PDF',
        description: 'Permanently remove sensitive text or regions from pages.',
        href: redactPdf.url(),
        icon: EyeOff,
        iconClass:
            'bg-slate-200 text-slate-800 dark:bg-slate-800 dark:text-slate-200',
    },
    {
        key: 'compare',
        title: 'Compare PDFs',
        description: 'See differences between two PDFs at a glance.',
        href: comparePdf.url(),
        icon: GitCompare,
        iconClass:
            'bg-slate-200 text-slate-800 dark:bg-slate-800 dark:text-slate-200',
    },
];

const toPdfToolsBase: ToolEntry[] = [
    {
        key: 'from_jpg',
        title: 'Images to PDF',
        description: 'Turn JPG, PNG, and other images into a single PDF.',
        href: fromJpgPdf.url(),
        icon: Image,
        iconClass:
            'bg-cyan-100 text-cyan-800 dark:bg-cyan-950/40 dark:text-cyan-300',
    },
    {
        key: 'from_word',
        title: 'Word to PDF',
        description: 'Convert Word documents (.docx) into PDF.',
        href: fromWordPdf.url(),
        icon: FileText,
        iconClass:
            'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
    },
    {
        key: 'from_powerpoint',
        title: 'PowerPoint to PDF',
        description: 'Export presentations (.pptx) as PDF slides.',
        href: fromPowerpointPdf.url(),
        icon: Presentation,
        iconClass:
            'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300',
    },
    {
        key: 'from_excel',
        title: 'Excel to PDF',
        description: 'Convert spreadsheets (.xlsx) into a printable PDF.',
        href: fromExcelPdf.url(),
        icon: Table2,
        iconClass:
            'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
    },
    {
        key: 'from_html',
        title: 'HTML to PDF',
        description:
            'Render HTML markup or pasted content as a PDF for archiving.',
        href: fromHtmlPdf.url(),
        icon: Code2,
        iconClass:
            'bg-brand-100 text-brand-700 dark:bg-brand-900/40 dark:text-brand-300',
    },
];

const fromPdfToolsBase: ToolEntry[] = [
    {
        key: 'to_jpg',
        title: 'PDF to JPG',
        description: 'Export each page as a high-quality JPG image.',
        href: toJpgPdf.url(),
        icon: ImageDown,
        iconClass:
            'bg-cyan-100 text-cyan-800 dark:bg-cyan-950/40 dark:text-cyan-300',
    },
    {
        key: 'to_word',
        title: 'PDF to Word',
        description: 'Convert PDF content into an editable Word document.',
        href: toWordPdf.url(),
        icon: FileText,
        iconClass:
            'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
    },
    {
        key: 'to_powerpoint',
        title: 'PDF to PowerPoint',
        description:
            'Generate a presentation from PDF pages where layout allows.',
        href: toPowerpointPdf.url(),
        icon: Presentation,
        iconClass:
            'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300',
    },
    {
        key: 'to_excel',
        title: 'PDF to Excel',
        description:
            'Recover tables into a spreadsheet where structure is clear.',
        href: toExcelPdf.url(),
        icon: FileSpreadsheet,
        iconClass:
            'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
    },
];

const organizeTools = computed(() => localizeToolEntries(organizeToolsBase));
const editPdfTools = computed(() => localizeToolEntries(editPdfToolsBase));
const securityPdfTools = computed(() =>
    localizeToolEntries(securityPdfToolsBase),
);
const toPdfTools = computed(() => localizeToolEntries(toPdfToolsBase));
const fromPdfTools = computed(() => localizeToolEntries(fromPdfToolsBase));

const sections = computed(() => [
    {
        headingKey: 'tools.sections.organize',
        headingFallback: 'Organize PDF',
        tools: organizeTools.value,
    },
    {
        headingKey: 'tools.sections.edit',
        headingFallback: 'Edit PDF',
        tools: editPdfTools.value,
    },
    {
        headingKey: 'tools.sections.security',
        headingFallback: 'Security',
        tools: securityPdfTools.value,
    },
    {
        headingKey: 'tools.sections.to_pdf',
        headingFallback: 'Convert to PDF',
        tools: toPdfTools.value,
    },
    {
        headingKey: 'tools.sections.from_pdf',
        headingFallback: 'Convert from PDF',
        tools: fromPdfTools.value,
    },
]);
</script>

<template>
    <ToolPageHead v-bind="seo" json-ld-head-key="ld-json-pdf-dashboard" />

    <div
        class="flex h-full flex-1 flex-col gap-8 overflow-x-auto rounded-xl p-3 sm:gap-10 sm:p-4 md:p-6"
        data-testid="pdf-site-home"
    >
        <section
            class="relative overflow-hidden rounded-2xl border border-border/80 bg-gradient-to-br from-slate-50 via-white to-slate-100/80 shadow-sm dark:border-blue-900/50 dark:bg-gradient-to-br dark:from-blue-950/80 dark:via-slate-950/70 dark:to-slate-950/90"
        >
            <div
                class="pointer-events-none absolute -top-24 -right-24 size-64 rounded-full bg-slate-200/50 blur-3xl dark:bg-blue-500/10"
                aria-hidden="true"
            />
            <div
                class="pointer-events-none absolute -bottom-20 -left-16 size-56 rounded-full bg-blue-100/40 blur-3xl dark:bg-blue-500/10"
                aria-hidden="true"
            />

            <div
                class="relative grid gap-6 p-4 sm:gap-8 sm:p-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,20rem)] lg:items-center lg:gap-10 lg:p-8"
            >
                <div
                    class="order-2 w-full space-y-4 sm:space-y-5 lg:order-1 lg:max-w-2xl"
                >
                    <p
                        class="text-center text-xs font-semibold tracking-wide text-blue-700 uppercase sm:text-left dark:text-blue-300"
                    >
                        {{
                            t(
                                'tools.dashboard.hero.eyebrow',
                                'Free online PDF tools',
                            )
                        }}
                    </p>
                    <div class="space-y-3 text-center sm:text-left">
                        <h1
                            class="text-2xl font-bold tracking-tight text-balance text-foreground sm:text-3xl lg:text-4xl"
                        >
                            {{
                                t(
                                    'tools.dashboard.hero.title',
                                    'Merge, split, compress, and convert PDFs in your browser',
                                )
                            }}
                        </h1>
                        <p
                            class="text-sm leading-relaxed text-pretty text-muted-foreground sm:text-base"
                        >
                            {{
                                t(
                                    'tools.dashboard.hero.lead',
                                    'Organize pages, reduce file size, convert to Office or images, and secure documents—without installing desktop software.',
                                )
                            }}
                        </p>
                    </div>
                    <ul class="space-y-2.5" role="list">
                        <li
                            v-for="(feature, index) in heroFeatures"
                            :key="index"
                            class="flex gap-2.5 text-sm text-muted-foreground"
                        >
                            <Check
                                class="mt-0.5 size-4 shrink-0 text-blue-600 dark:text-blue-400"
                                aria-hidden="true"
                            />
                            <span class="text-pretty">{{ feature }}</span>
                        </li>
                    </ul>
                    <div
                        class="flex flex-wrap justify-center gap-2.5 pt-1 sm:justify-start"
                    >
                        <Button
                            v-for="cta in heroCtas"
                            :key="cta.href"
                            :variant="cta.variant"
                            :class="
                                cta.variant === 'default'
                                    ? 'bg-blue-600 text-white hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-500'
                                    : 'border-border bg-background hover:bg-muted dark:border-blue-800/60 dark:bg-blue-950/40 dark:hover:bg-blue-950/70'
                            "
                            as-child
                        >
                            <Link :href="cta.href" prefetch>
                                {{ cta.label }}
                                <ArrowRight
                                    v-if="cta.variant === 'default'"
                                    class="size-4"
                                    aria-hidden="true"
                                />
                            </Link>
                        </Button>
                    </div>
                </div>

                <div class="order-1 w-full lg:order-2 lg:justify-self-end">
                    <div
                        class="flex w-full flex-col gap-3 rounded-2xl border border-border/80 bg-card p-4 shadow-sm sm:gap-4 sm:p-6 lg:max-w-[20rem] lg:p-8 dark:border-white/10 dark:bg-white/5 dark:backdrop-blur-sm"
                    >
                        <div
                            class="flex items-center gap-4 sm:flex-col sm:items-center sm:gap-4"
                        >
                            <img
                                src="/images/pdf/android-chrome-192x192.png"
                                alt=""
                                width="120"
                                height="120"
                                class="size-16 shrink-0 object-contain sm:size-[7.5rem]"
                                loading="eager"
                                decoding="async"
                            />
                            <div
                                class="min-w-0 flex-1 space-y-1.5 text-left sm:w-full sm:text-center"
                            >
                                <p
                                    class="inline-flex min-w-0 items-baseline gap-[2px] text-base font-bold tracking-tight select-none sm:justify-center sm:text-lg"
                                >
                                    <span
                                        class="text-neutral-950 dark:text-neutral-50"
                                        >pdf</span
                                    >
                                    <span
                                        class="text-blue-600 dark:text-blue-400"
                                        >easy</span
                                    >
                                    <sup
                                        class="[vertical-align:0.35em] align-baseline text-[0.42em] leading-none font-semibold text-neutral-600 dark:text-neutral-400"
                                        >.app</sup
                                    >
                                </p>
                                <p
                                    class="text-sm leading-relaxed font-medium text-pretty text-foreground"
                                >
                                    {{
                                        t(
                                            'tools.dashboard.hero.brand_tagline',
                                            'Everything you need to work with PDFs for work, school, and everyday tasks—right in the browser.',
                                        )
                                    }}
                                </p>
                            </div>
                        </div>
                        <p
                            class="text-xs leading-relaxed text-pretty text-muted-foreground sm:text-center"
                        >
                            {{
                                t(
                                    'tools.dashboard.hero.brand_note',
                                    'Upload, download when ready, and we remove your files after the job.',
                                )
                            }}
                        </p>
                    </div>
                </div>
            </div>
        </section>

        <p
            class="max-w-3xl text-xs leading-relaxed text-pretty text-muted-foreground"
        >
            {{
                t(
                    'tools.data_retention_notice',
                    'We do not keep your uploads after a tool run finishes: files are stored only temporarily to perform the operation, then deleted from the server (generated downloads are removed after they are sent, where applicable).',
                )
            }}
        </p>

        <div class="space-y-8">
            <h2
                class="text-sm font-medium tracking-wider text-muted-foreground uppercase"
            >
                {{ t('tools.dashboard.all_tools_heading', 'All tools') }}
            </h2>

            <div
                v-for="section in sections"
                :key="section.headingKey"
                class="space-y-4"
            >
                <h3
                    class="text-sm font-medium tracking-wider text-muted-foreground uppercase"
                >
                    {{ t(section.headingKey, section.headingFallback) }}
                </h3>
                <div
                    class="grid gap-3 sm:grid-cols-2 sm:gap-4 xl:grid-cols-3 2xl:grid-cols-4"
                >
                    <Link
                        v-for="tool in section.tools"
                        :key="tool.key"
                        :href="tool.href"
                        prefetch
                        class="group touch-manipulation ring-offset-background outline-none focus-visible:rounded-xl focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    >
                        <Card
                            class="h-full border-sidebar-border/70 transition-colors hover:border-blue-200/80 hover:bg-accent/40 dark:border-sidebar-border dark:hover:border-blue-800/50"
                        >
                            <CardHeader class="gap-3">
                                <div
                                    :class="[
                                        'flex size-11 shrink-0 items-center justify-center rounded-lg transition-colors group-hover:brightness-95',
                                        tool.iconClass,
                                    ]"
                                >
                                    <component :is="tool.icon" class="size-5" />
                                </div>
                                <div class="space-y-1.5">
                                    <CardTitle
                                        class="text-base leading-snug group-hover:text-foreground"
                                    >
                                        {{
                                            t(
                                                `tools.pdf.${tool.key}.title`,
                                                tool.title,
                                            )
                                        }}
                                    </CardTitle>
                                    <CardDescription
                                        class="line-clamp-3 text-xs leading-relaxed sm:text-sm"
                                    >
                                        {{
                                            t(
                                                `tools.pdf.${tool.key}.description`,
                                                tool.description,
                                            )
                                        }}
                                    </CardDescription>
                                </div>
                            </CardHeader>
                        </Card>
                    </Link>
                </div>
            </div>
        </div>
    </div>
</template>
